<template>
  <div class="hms-page">
    <Navigation />

    <div class="container-fluid hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-shield-search me-2"></i>{{ $t('page.audit_log', 'Audit Log') }}</h4>
        </div>
        <div class="d-flex gap-2 flex-wrap">
          <button class="hms-btn hms-btn-blue" @click="exportCsv" :disabled="!logs.length"><i class="mdi mdi-download me-1"></i>Export CSV</button>
          <button class="hms-btn hms-btn-blue" @click="loadAll" :disabled="loading">
            <i class="mdi mdi-refresh" :class="{ 'mdi-spin': loading }"></i>
          </button>
        </div>
      </div>

      <!-- Stats -->
      <div class="hms-stat-row">
        <div class="hms-stat">
          <i class="mdi mdi-database hms-stat-icon"></i>
          <div class="hms-stat-num">{{ formatNum(stats.total) }}</div>
          <div class="hms-stat-lbl">Total Events</div>
        </div>
        <div class="hms-stat">
          <i class="mdi mdi-clock-outline hms-stat-icon hms-stat-i-info"></i>
          <div class="hms-stat-num hms-stat-info">{{ formatNum(stats.today) }}</div>
          <div class="hms-stat-lbl">Today</div>
        </div>
        <div class="hms-stat">
          <i class="mdi mdi-calendar-week hms-stat-icon hms-stat-i-success"></i>
          <div class="hms-stat-num hms-stat-success">{{ formatNum(stats.last_7d) }}</div>
          <div class="hms-stat-lbl">Last 7 Days</div>
        </div>
        <div class="hms-stat">
          <i class="mdi mdi-account-multiple hms-stat-icon hms-stat-i-purple"></i>
          <div class="hms-stat-num hms-stat-purple">{{ formatNum(stats.active_users_today) }}</div>
          <div class="hms-stat-lbl">Active Users Today</div>
        </div>
      </div>

      <div class="row g-3 mb-3">
        <!-- BY MODULE -->
        <div class="col-md-7">
          <div class="hms-card h-100">
            <div class="hms-card-title"><i class="mdi mdi-chart-donut me-1"></i>Activity by Module (last 7 days)</div>
            <div v-if="byModule.length" class="hms-rank-list">
              <div v-for="m in byModule" :key="m.module" class="hms-rank-row" @click="filters.module = m.module; loadAll()">
                <div class="flex-fill">
                  <div class="hms-mono">{{ m.module }}</div>
                  <div class="hms-bar"><div class="hms-bar-fill" :style="{ width: pct(m.cnt, byModule[0].cnt) + '%' }"></div></div>
                </div>
                <span class="hms-rank-val">{{ formatNum(m.cnt) }}</span>
              </div>
            </div>
            <div v-else class="hms-muted">No activity in the last 7 days.</div>
          </div>
        </div>

        <!-- TOP USERS -->
        <div class="col-md-5">
          <div class="hms-card h-100">
            <div class="hms-card-title"><i class="mdi mdi-account-star-outline me-1"></i>Most Active Users (last 7 days)</div>
            <div v-if="topUsers.length" class="hms-rank-list">
              <div v-for="(u, i) in topUsers" :key="u.user_id" class="hms-rank-row" @click="filters.user_id = u.user_id; loadAll()">
                <span class="hms-rank">{{ i + 1 }}</span>
                <div class="flex-fill">
                  <div class="hms-text fw-semibold">{{ u.user_name || u.username || ('User #' + u.user_id) }}</div>
                  <div class="hms-bar"><div class="hms-bar-fill bar-purple" :style="{ width: pct(u.cnt, topUsers[0].cnt) + '%' }"></div></div>
                </div>
                <span class="hms-rank-val">{{ formatNum(u.cnt) }}</span>
              </div>
            </div>
            <div v-else class="hms-muted">No user activity yet.</div>
          </div>
        </div>
      </div>

      <!-- Filters + Log -->
      <div class="hms-card">
        <div class="d-flex flex-wrap gap-2 mb-3 align-items-center">
          <div class="hms-search-wrap">
            <i class="mdi mdi-magnify"></i>
            <input class="hms-search" v-model="filters.search" placeholder="Search description, action, module..." @input="debouncedLoad" />
          </div>
          <select class="hms-input hms-filter" v-model="filters.module" @change="loadAll">
            <option value="">All modules</option>
            <option v-for="m in facets.modules" :key="m" :value="m">{{ m }}</option>
          </select>
          <select class="hms-input hms-filter" v-model="filters.action" @change="loadAll">
            <option value="">All actions</option>
            <option v-for="a in facets.actions" :key="a" :value="a">{{ a }}</option>
          </select>
          <select class="hms-input hms-filter" v-model="filters.user_id" @change="loadAll">
            <option value="">All users</option>
            <option v-for="u in users" :key="u.user_id" :value="u.user_id">{{ u.first_name }} {{ u.last_name }}</option>
          </select>
          <input type="date" class="hms-input hms-filter" v-model="filters.date_from" @change="loadAll" :max="todayISO" />
          <span class="hms-muted-sm">to</span>
          <input type="date" class="hms-input hms-filter" v-model="filters.date_to"   @change="loadAll" :max="todayISO" />
          <button class="hms-btn hms-btn-amber" v-if="hasFilters" @click="resetFilters">
            <i class="mdi mdi-filter-off-outline me-1"></i>Clear
          </button>
          <span class="hms-muted ms-auto">{{ logs.length }} {{ logs.length === 200 ? '(capped)' : '' }} events</span>
        </div>

        <div v-if="loading && !logs.length" class="hms-spinner"><div class="spinner-border"></div></div>

        <div v-else-if="logs.length" style="overflow-x:auto;">
          <table class="hms-table">
            <thead><tr>
              <th>When</th>
              <th>User</th>
              <th>Module</th>
              <th>Action</th>
              <th>Description</th>
              <th>Facility</th>
              <th>IP</th>
              <th></th>
            </tr></thead>
            <tbody>
              <tr v-for="l in logs" :key="l.log_id" @click="openDetail(l)" class="hms-row-click">
                <td><div class="hms-muted-sm">{{ formatDateTime(l.created_at) }}</div><div class="hms-muted-sm">{{ relativeTime(l.created_at) }}</div></td>
                <td>
                  <div class="hms-text fw-semibold">{{ l.user_name || l.username || '—' }}</div>
                  <div class="hms-muted-sm" v-if="l.role_name">{{ l.role_name }}</div>
                </td>
                <td><span class="hms-badge hms-badge-info">{{ l.module || '—' }}</span></td>
                <td><span class="hms-mono">{{ l.action || '—' }}</span></td>
                <td><div class="hms-clip">{{ l.description || '—' }}</div></td>
                <td><div class="hms-muted-sm">{{ l.branch_name || '—' }}</div></td>
                <td><div class="hms-muted-sm hms-mono">{{ l.ip_address || '—' }}</div></td>
                <td><i class="mdi mdi-chevron-right text-muted"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="hms-empty"><i class="mdi mdi-shield-search-outline"></i>No events match.</div>
      </div>

      <!-- DETAIL MODAL -->
      <div class="hms-modal-overlay" v-if="detail" @click.self="detail=null">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-shield-search me-1"></i>Event #{{ detail.log_id }}</span>
            <button class="hms-modal-close" @click="detail=null">&times;</button>
          </div>

          <div class="hms-kv mb-3">
            <div><span class="hms-kv-key">When</span><span>{{ formatDateTime(detail.created_at) }} <span class="hms-muted-sm">({{ relativeTime(detail.created_at) }})</span></span></div>
            <div><span class="hms-kv-key">User</span><span><strong>{{ detail.user_name || detail.username || '—' }}</strong> <span class="hms-muted-sm" v-if="detail.role_name">· {{ detail.role_name }}</span></span></div>
            <div><span class="hms-kv-key">Facility</span><span>{{ detail.branch_name || '—' }}</span></div>
            <div><span class="hms-kv-key">Module</span><span><span class="hms-badge hms-badge-info">{{ detail.module || '—' }}</span></span></div>
            <div><span class="hms-kv-key">Action</span><span class="hms-mono">{{ detail.action || '—' }}</span></div>
            <div v-if="detail.record_id"><span class="hms-kv-key">Record ID</span><span class="hms-mono">{{ detail.record_id }}</span></div>
            <div><span class="hms-kv-key">IP Address</span><span class="hms-mono">{{ detail.ip_address || '—' }}</span></div>
            <div v-if="detail.user_agent"><span class="hms-kv-key">User Agent</span><span class="hms-text-clip-md">{{ detail.user_agent }}</span></div>
          </div>

          <div class="hms-section-label">DESCRIPTION</div>
          <div class="hms-pre-box mb-3">{{ detail.description || '(no description)' }}</div>

          <div v-if="detail.old_values">
            <div class="hms-section-label">PREVIOUS VALUES</div>
            <pre class="hms-pre-box hms-pre-json">{{ formatJson(detail.old_values) }}</pre>
          </div>
          <div v-if="detail.new_values">
            <div class="hms-section-label">NEW VALUES</div>
            <pre class="hms-pre-box hms-pre-json">{{ formatJson(detail.new_values) }}</pre>
          </div>

          <div class="d-flex gap-2 mt-3">
            <button class="hms-btn hms-btn-blue flex-fill" @click="filters.user_id = detail.user_id; detail = null; loadAll()">
              <i class="mdi mdi-account-search me-1"></i>All events by this user
            </button>
            <button class="hms-btn hms-btn-blue flex-fill" @click="filters.module = detail.module; detail = null; loadAll()" v-if="detail.module">
              <i class="mdi mdi-filter me-1"></i>All in {{ detail.module }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
:root, html[data-theme="dark"] {
  --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;
  --hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;
  --hms-br:#2a2a2a;--hms-input-br:#333333;
  --hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] {
  --hms-h:#1a1a1a;--hms-s:#555555;--hms-m:#777777;
  --hms-bg1:#ffffff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#ffffff;
  --hms-br:#dee2e8;--hms-input-br:#ced4da;
  --hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding: 20px 24px 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }

.hms-text { color: var(--hms-h); }
.hms-muted { color: var(--hms-s); font-size: 13px; }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }
.text-muted { color: var(--hms-m); }
.hms-clip { max-width: 360px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.hms-text-clip-md { display: inline-block; max-width: 400px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; vertical-align: top; }

.hms-stat-row { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 18px; }
.hms-stat { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 12px; padding: 14px 18px; flex: 1; min-width: 160px; }
.hms-stat-icon { font-size: 24px; color: var(--hms-primary); margin-bottom: 4px; display: block; }
.hms-stat-i-info    { color: var(--hms-info); }
.hms-stat-i-success { color: var(--hms-success); }
.hms-stat-i-purple  { color: #9b59b6; }
.hms-stat-num { font-size: 22px; font-weight: 800; color: var(--hms-h); line-height: 1.1; }
.hms-stat-lbl { font-size: 11px; color: var(--hms-s); margin-top: 4px; text-transform: uppercase; letter-spacing: 0.5px; }
.hms-stat-info    { color: var(--hms-info); }
.hms-stat-success { color: var(--hms-success); }
.hms-stat-purple  { color: #9b59b6; }

.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 18px; }
.hms-card-title { color: var(--hms-h); font-weight: 700; font-size: 13px; margin-bottom: 12px; text-transform: uppercase; letter-spacing: 1px; }
.hms-spinner { display: flex; justify-content: center; padding: 40px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }
.hms-empty { text-align: center; padding: 40px 20px; color: var(--hms-s); }
.hms-empty i { font-size: 48px; color: var(--hms-br); display: block; margin-bottom: 12px; }

.hms-search-wrap { position: relative; flex: 1; min-width: 220px; max-width: 340px; }
.hms-search-wrap i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--hms-s); }
.hms-search { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px 8px 36px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-search:focus { border-color: var(--hms-primary); }
.hms-input { background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 7px 12px; color: var(--hms-h); font-size: 13px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-filter { width: auto; }

.hms-rank-list { display: flex; flex-direction: column; gap: 8px; }
.hms-rank-row { display: flex; gap: 10px; align-items: center; cursor: pointer; padding: 4px; border-radius: 6px; transition: all .15s; }
.hms-rank-row:hover { background: var(--hms-primary-lt); }
.hms-rank { width: 24px; height: 24px; border-radius: 50%; background: var(--hms-bg3); color: var(--hms-h); display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 800; flex-shrink: 0; }
.hms-rank-val { color: var(--hms-h); font-weight: 700; font-size: 13px; flex-shrink: 0; }
.hms-bar { height: 6px; background: var(--hms-bg3); border-radius: 50px; overflow: hidden; }
.hms-bar-fill { height: 100%; background: var(--hms-primary); transition: width .3s; }
.hms-bar-fill.bar-purple { background: #9b59b6; }

.hms-table { width: 100%; border-collapse: collapse; }
.hms-table th { color: var(--hms-s); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); text-align: left; white-space: nowrap; }
.hms-table td { color: var(--hms-h); font-size: 13px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); vertical-align: top; }
.hms-row-click { cursor: pointer; }
.hms-row-click:hover td { background: var(--hms-primary-lt); }

.hms-badge { padding: 2px 10px; border-radius: 50px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; display: inline-block; }
.hms-badge-info { background: var(--hms-primary-lt); color: var(--hms-primary); }

.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; justify-content: center; }
.hms-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }
.hms-btn-amber { background: rgba(245,158,11,.15); color: #f59e0b; border: 1px solid rgba(245,158,11,.3); }
.hms-btn-amber:hover:not(:disabled) { background: rgba(245,158,11,.25); }

.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.6); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; overflow-y: auto; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 16px; padding: 24px; width: 100%; max-width: 760px; max-height: calc(100vh - 80px); overflow-y: auto; }
.hms-modal-title { color: var(--hms-h); font-size: 18px; font-weight: 700; margin-bottom: 18px; display: flex; align-items: center; justify-content: space-between; }
.hms-modal-close { color: var(--hms-s); cursor: pointer; font-size: 24px; line-height: 1; background: none; border: none; padding: 0 4px; }

.hms-section-label { font-size: 11px; font-weight: 700; color: var(--hms-s); letter-spacing: 1.5px; margin-bottom: 8px; }
.hms-kv { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 12px 16px; }
.hms-kv > div { display: flex; padding: 6px 0; border-bottom: 1px dashed var(--hms-br); font-size: 13px; color: var(--hms-h); gap: 8px; }
.hms-kv > div:last-child { border-bottom: none; }
.hms-kv-key { color: var(--hms-s); flex: 0 0 130px; font-weight: 600; }
.hms-pre-box { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 8px; padding: 12px 14px; color: var(--hms-h); font-size: 13px; white-space: pre-wrap; }
.hms-pre-json { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; max-height: 280px; overflow-y: auto; }
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "AuditLogViewer",
  components: { Navigation },
  data() {
    return {
      loading: true,
      logs: [], stats: {}, byModule: [], topUsers: [],
      facets: { modules: [], actions: [] },
      users: [],
      filters: { search: '', module: '', action: '', user_id: '', date_from: '', date_to: '' },
      _searchTimer: null,
      detail: null
    };
  },
  computed: {
    todayISO() { return new Date().toISOString().slice(0, 10); },
    hasFilters() {
      var f = this.filters;
      return !!(f.search || f.module || f.action || f.user_id || f.date_from || f.date_to);
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
    document.documentElement.setAttribute("data-theme", t);
    this.loadLookups();
    this.loadAll();
  },
  beforeUnmount() { if (this._searchTimer) clearTimeout(this._searchTimer); },
  methods: {
    async loadLookups() {
      try {
        var res = await Promise.all([
          executeJson({ function: "getAuditFacets" }, hms),
          executeJson({ function: "getUsers" }, hms)
        ]);
        if (res[0].data && res[0].data.success) this.facets = res[0].data.data || { modules: [], actions: [] };
        if (res[1].data && res[1].data.success) this.users  = (res[1].data.data && res[1].data.data.Users) || [];
      } catch(e) {}
    },
    async loadAll() {
      this.loading = true;
      try {
        var payload = Object.assign({ function: "getAuditLogs" }, this.filters);
        var r = await executeJson(payload, hms);
        if (r.data && r.data.success) {
          this.logs     = (r.data.data && r.data.data.Logs)     || [];
          this.stats    = (r.data.data && r.data.data.Stats)    || {};
          this.byModule = (r.data.data && r.data.data.ByModule) || [];
          this.topUsers = (r.data.data && r.data.data.TopUsers) || [];
        }
      } catch(e) { console.error(e); }
      this.loading = false;
    },
    debouncedLoad() {
      if (this._searchTimer) clearTimeout(this._searchTimer);
      var self = this;
      this._searchTimer = setTimeout(() => self.loadAll(), 350);
    },
    resetFilters() {
      this.filters = { search: '', module: '', action: '', user_id: '', date_from: '', date_to: '' };
      this.loadAll();
    },
    openDetail(l) { this.detail = l; },

    formatNum(n) { if (n == null) return '0'; return Number(n).toLocaleString(); },
    formatDateTime(s) { if (!s) return '—'; var d = new Date(s.replace ? s.replace(' ','T') : s); return isNaN(d) ? s : d.toLocaleString(); },
    relativeTime(s) {
      if (!s) return ''; var d = new Date(s.replace ? s.replace(' ','T') : s); if (isNaN(d)) return '';
      var diff = (Date.now() - d.getTime()) / 1000;
      if (diff < 60) return Math.floor(diff) + 's ago';
      if (diff < 3600) return Math.floor(diff/60) + 'm ago';
      if (diff < 86400) return Math.floor(diff/3600) + 'h ago';
      if (diff < 604800) return Math.floor(diff/86400) + 'd ago';
      return d.toLocaleDateString();
    },
    pct(part, whole) {
      var p = parseFloat(part) || 0, w = parseFloat(whole) || 0;
      if (w <= 0) return 0;
      return Math.round((p / w) * 100);
    },
    formatJson(s) {
      if (!s) return '';
      try { return JSON.stringify(JSON.parse(s), null, 2); } catch(e) { return s; }
    },

    exportCsv() {
      if (!this.logs.length) return;
      var headers = ['log_id','created_at','user_name','username','role_name','branch_name','module','action','description','ip_address'];
      var rows = this.logs.map(l => headers.map(h => {
        var v = l[h] == null ? '' : String(l[h]);
        if (v.indexOf(',') >= 0 || v.indexOf('"') >= 0 || v.indexOf('\n') >= 0) v = '"' + v.replace(/"/g, '""') + '"';
        return v;
      }).join(','));
      var csv = headers.join(',') + '\n' + rows.join('\n');
      var blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      var link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'audit-log-' + new Date().toISOString().slice(0,10) + '.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
};
</script>
